import { isBefore, compareAsc } from "date-fns";
import { useCallback, useMemo } from "react";
import { ClientByTokenQuery } from "@/graphql/queries/clientByToken.graphql.types";
import useMedspaTimezone from "@/hooks/common/useMedspaTimezone";
import { VisitStatus } from "@/types";

type Visit =
  ClientByTokenQuery["clientAccessTokenByPk"]["client"]["visits"][number];

const isCancelledVisit = (visit: Visit) =>
  [
    VisitStatus.CANCELLED,
    VisitStatus.CANCELLED_LATE,
    VisitStatus.NO_SHOW,
  ].includes(visit.status as VisitStatus);

export default function useSortClientVisits(visits: Visit[]) {
  const medspaId = visits?.length ? visits[0].medspa.id : undefined;
  const { utcToZonedTimeIso, getMedspaCurrentTime } =
    useMedspaTimezone(medspaId);

  const isPastVisit = useCallback(
    (visit: Visit) =>
      visit.status === VisitStatus.COMPLETED ||
      (!isCancelledVisit(visit) &&
        isBefore(
          new Date(visit.appointment?.startTime ?? visit.startTime),
          getMedspaCurrentTime()
        )),
    [getMedspaCurrentTime]
  );

  const isFutureVisit = useCallback(
    (visit: Visit) => !isCancelledVisit(visit) && !isPastVisit(visit),
    [isPastVisit]
  );

  const { cancelledVisits, pastVisits, futureVisits } = useMemo(() => {
    if (!visits) {
      return { cancelledVisits: [], pastVisits: [], futureVisits: [] };
    }

    const mappedVisits = visits.map((visit) => ({
      ...visit,
      appointment: {
        ...visit.appointment,
        startTime: utcToZonedTimeIso(
          visit.appointment?.startTime ?? visit.startTime
        ),
        endTime: utcToZonedTimeIso(visit.appointment?.endTime),
      },
    }));

    return {
      cancelledVisits: mappedVisits.filter(isCancelledVisit),
      pastVisits: mappedVisits.filter(isPastVisit),
      futureVisits: mappedVisits
        .filter(isFutureVisit)
        // Graphql returns visits in descending order - future visits should be displayed in ascending order
        .sort((a, b) =>
          compareAsc(
            new Date(a.appointment?.startTime ?? a.startTime),
            new Date(b.appointment?.startTime ?? b.startTime)
          )
        ),
    };
  }, [visits, utcToZonedTimeIso, isPastVisit, isFutureVisit]);

  return { cancelledVisits, pastVisits, futureVisits };
}
